"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.startClearForceApproveObject = exports.startForceApproveObject = exports.startRequestReReview = exports.startClearObjectReview = exports.startSubmitObjectReview = exports.startFetchInterviewReviewDecisions = exports.startResetCorrectedInterview = exports.startFetchCorrectedInterviewAndAudits = exports.startSurveyCorrectedRemoveGroupedObjects = exports.startSurveyCorrectedAddGroupedObjects = exports.startSetSurveyCorrectedInterview = exports.startNavigateCorrectedInterview = exports.startUpdateSurveyCorrectedInterview = void 0;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
/* eslint-disable-next-line */
const fetchRetry = require('@zeit/fetch-retry')(require('node-fetch'));
// TODO Default options for retry are as high as 15 seconds, during which the
// user get no feedback. Since update requests are queued in that time, are
// sequential and it is not possible to empty the queue, the requests will try
// to execute for n*15 seconds, during which time the user may have refreshed
// the page and will not see that the updated values have been udpated. With 4
// retries, the user will get feedback within 3 seconds and the queue will empty
// much faster, reducing the risk of invisible updates. The risk is still
// present if the server is quickly back online and the user is fast enough.
const fetch = (url, opts) => __awaiter(void 0, void 0, void 0, function* () {
    return yield fetchRetry(url, Object.assign(Object.assign({ retry: { retries: 4 } }, opts)));
});
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const surveyHelper = __importStar(require("evolution-common/lib/utils/helpers"));
const SurveyObjectsUnserializer_1 = require("../utils/SurveyObjectsUnserializer");
const types_1 = require("../store/survey/types");
const LoadingState_1 = require("./LoadingState");
const errorHandling_1 = require("../services/errorManagement/errorHandling");
const i18n_config_1 = __importDefault(require("../config/i18n.config"));
const sonner_1 = require("sonner");
const Survey_1 = require("./Survey");
// Extended type for admin interviews. `surveyObjectsAndAudits` holds the payload from
// `correctInterview` (audits only), unserialized in place into survey object instances
// (see `unserializeSurveyObjectsAndAudits`). Review decisions are a separate layer stored
// in `state.survey.reviewDecisions`, not on the interview.
/**
 * Helper function to unserialize the `surveyObjectsAndAudits` payload received from
 * `correctInterview` (audits only) into survey object instances, in place.
 * The raw payload is removed when it is missing, invalid, or fails to unserialize.
 *
 * @param interview - The interview containing surveyObjectsAndAudits to unserialize
 * @param errorContext - Optional context string to include in error message (e.g., 'for reset')
 */
const unserializeSurveyObjectsAndAudits = (interview, errorContext = '') => {
    const surveyObjectsAndAudits = interview.surveyObjectsAndAudits;
    delete interview.surveyObjectsAndAudits;
    const contextSuffix = errorContext ? ` ${errorContext}` : '';
    if (!surveyObjectsAndAudits || !SurveyObjectsUnserializer_1.SurveyObjectsUnserializer.hasValidData(surveyObjectsAndAudits)) {
        console.error(`Failed to unserialize surveyObjectsAndAudits${contextSuffix}: missing or invalid payload`);
        return;
    }
    try {
        const unserialized = SurveyObjectsUnserializer_1.SurveyObjectsUnserializer.unserialize(surveyObjectsAndAudits);
        if (!unserialized || !SurveyObjectsUnserializer_1.SurveyObjectsUnserializer.hasValidData(unserialized)) {
            console.error(`Failed to unserialize surveyObjectsAndAudits${contextSuffix}: invalid or empty result`);
            return;
        }
        interview.surveyObjectsAndAudits = unserialized;
    }
    catch (error) {
        console.error(`Failed to unserialize surveyObjectsAndAudits${contextSuffix}:`, error);
    }
};
// dispatch callback to do the interview state update and call to server. To be
// called by the `startUpdateSurveyCorrectedInterview` action and any other
// dispatched action requiring to update the interview fields and the server.
// FIXME The code in this function is not totally identical to its counterpart
// in Survey.ts (no server validations or side effects). Should it be? If so, it
// only differs by the URL called in the backend. Refactor to make code re-use
// easier.
const updateSurveyCorrectedInterview = (dispatch_1, getState_1, ...args_1) => __awaiter(void 0, [dispatch_1, getState_1, ...args_1], void 0, function* (dispatch, getState, { sectionShortname: requestedSectionShortname, valuesByPath = {}, unsetPaths = [], interview: initialInterview, userAction } = {}, callback) {
    var _a;
    try {
        const { survey } = getState();
        const navigation = survey === null || survey === void 0 ? void 0 : survey.navigation;
        const interview = initialInterview
            ? initialInterview
            : (0, cloneDeep_1.default)(survey === null || survey === void 0 ? void 0 : survey.interview);
        dispatch((0, LoadingState_1.incrementLoadingState)());
        if (valuesByPath && Object.keys(valuesByPath).length > 0) {
            surveyHelper.devLog('Update interview and section with values by path', JSON.parse(JSON.stringify(valuesByPath)));
        }
        const affectedPaths = (0, Survey_1.updateInterviewData)(interview, { valuesByPath, unsetPaths, userAction });
        // TODO: update this so it works well with validationOnePager (admin). Should we force this? Anyway this code should be replaced/updated completely in the next version.
        const sectionShortname = requestedSectionShortname
            ? requestedSectionShortname
            : ((_a = navigation === null || navigation === void 0 ? void 0 : navigation.currentSection) === null || _a === void 0 ? void 0 : _a.sectionShortname) || '';
        // Skip section validation for admin interface - we don't need widget preparation
        let updatedInterview;
        let updatedValuesByPath;
        if (!sectionShortname) {
            // For admin interface, just update the interview data without section validation
            updatedInterview = interview;
            updatedValuesByPath = valuesByPath;
        }
        else {
            // For regular survey sections, use the full validation
            [updatedInterview, updatedValuesByPath] = (0, Survey_1.validateAndPrepareSection)(sectionShortname, interview, affectedPaths, valuesByPath);
        }
        if (!updatedInterview.sectionLoaded || updatedInterview.sectionLoaded !== sectionShortname) {
            updatedValuesByPath['sectionLoaded'] = sectionShortname;
            updatedInterview.sectionLoaded = sectionShortname;
        }
        // convert undefined values to unset (delete) because stringify will remove undefined values:
        for (const path in updatedValuesByPath) {
            if (updatedValuesByPath[path] === undefined) {
                unsetPaths.push(path);
            }
        }
        if ((0, isEqual_1.default)(updatedValuesByPath, { _all: true }) && (0, LodashExtensions_1._isBlank)(unsetPaths) && (0, LodashExtensions_1._isBlank)(userAction)) {
            dispatch((0, Survey_1.updateInterviewState)((0, cloneDeep_1.default)(updatedInterview), {}, true));
            if (typeof callback === 'function') {
                callback(updatedInterview);
            }
            return null;
        }
        //const differences = surveyHelper.differences(interview.response, oldInterview.response);
        const response = yield fetch(`/api/survey/updateCorrectedInterview/${interview.uuid}`, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({
                id: updatedInterview.id,
                participant_id: updatedInterview.participant_id,
                valuesByPath: updatedValuesByPath,
                unsetPaths: unsetPaths,
                userAction
            })
        });
        if (response.status === 200) {
            const body = yield response.json();
            if (body.status === 'success' && body.interviewId === updatedInterview.uuid) {
                //surveyHelper.devLog('Interview saved to db');
                //setTimeout(function() {
                dispatch((0, Survey_1.updateInterviewState)((0, cloneDeep_1.default)(updatedInterview), {}, updatedValuesByPath['_all'] === true));
                if (typeof callback === 'function') {
                    callback(updatedInterview);
                }
                //}, 500, 'That was really slow!');
            }
            else {
                // TODO we need to do something if no interview is returned (error)
            }
        }
        else {
            console.error(`startUpdateSurveyCorrectedInterview: wrong response status: ${response.status}`);
            (0, errorHandling_1.handleHttpOtherResponseCode)(response.status, dispatch);
        }
    }
    catch (error) {
        console.error('Error updating interview', error);
    }
    finally {
        // Loading state needs to be decremented, so the page can be updated
        dispatch((0, LoadingState_1.decrementLoadingState)());
    }
});
/**
 * Redux action to call with a dispatch to send corrected interview updates to
 * the server.  This function is not to be called directly by the application,
 * except through redux's dispatch.
 *
 * It will schedule the call to the interview update callback who runs
 * validations and side effects, updates the interview data on the server and
 * refresh any fields and validations coming from the server. It will update the
 * app's loading state before and after the update call. At the end of the call,
 * the callback function will be called if provided, with the updated interview.
 * @returns The dispatched action
 */
const startUpdateSurveyCorrectedInterview = (data, callback) => (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
    return yield Survey_1.asyncDispatchQueue.enqueueTask(() => __awaiter(void 0, void 0, void 0, function* () {
        yield updateSurveyCorrectedInterview(dispatch, getState, data, callback);
    }));
});
exports.startUpdateSurveyCorrectedInterview = startUpdateSurveyCorrectedInterview;
/**
 * Redux action to call with a dispatch to navigate forward in the current
 * interview for corrected interviews
 *
 * @returns The dispatched action
 */
const startNavigateCorrectedInterview = (options = {}, callback) => (0, Survey_1.startNavigateWithUpdateCallback)(exports.startUpdateSurveyCorrectedInterview, options, callback);
exports.startNavigateCorrectedInterview = startNavigateCorrectedInterview;
/**
 * Fetch an interview from server and set it for edition in correction mode.
 *
 * @param {*} interviewUuid The uuid of the interview to open
 * @param {*} callback
 * @returns
 */
// TODO: unit test
const startSetSurveyCorrectedInterview = (interviewUuid, callback = function () {
    return;
}) => {
    return (dispatch, _getState) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(`/api/survey/activeCorrectedInterview/${interviewUuid}`, {
                credentials: 'include'
            });
            if (response.status === 200) {
                const body = yield response.json();
                if (body.interview) {
                    const interview = body.interview;
                    // Set the interview in the state first
                    dispatch((0, Survey_1.updateInterviewState)(interview));
                    // Then, initialize navigation for the current interview
                    // This will handle all necessary updates internally
                    yield dispatch((0, exports.startNavigateCorrectedInterview)(undefined, callback));
                }
            }
        }
        catch (err) {
            surveyHelper.devLog('Error fetching interview to correct.', err);
        }
    });
};
exports.startSetSurveyCorrectedInterview = startSetSurveyCorrectedInterview;
// TODO: unit test
const startSurveyCorrectedAddGroupedObjects = (newObjectsCount, insertSequence, path, attributes = [], callback, returnOnly = false) => {
    surveyHelper.devLog(`Add ${newObjectsCount} grouped objects for path ${path} at sequence ${insertSequence}`);
    return (dispatch, getState) => {
        const interview = (0, cloneDeep_1.default)(getState().survey.interview); // needed because we cannot mutate state
        const changedValuesByPath = surveyHelper.addGroupedObjects(interview, newObjectsCount, insertSequence, path, attributes || []);
        if (returnOnly) {
            return changedValuesByPath;
        }
        else {
            dispatch((0, exports.startUpdateSurveyCorrectedInterview)({ valuesByPath: changedValuesByPath }, callback));
        }
    };
};
exports.startSurveyCorrectedAddGroupedObjects = startSurveyCorrectedAddGroupedObjects;
// TODO: unit test
const startSurveyCorrectedRemoveGroupedObjects = (paths, callback, returnOnly = false) => {
    surveyHelper.devLog('Remove grouped objects at paths', paths);
    return (dispatch, getState) => {
        const interview = (0, cloneDeep_1.default)(getState().survey.interview); // needed because we cannot mutate state
        let unsetPaths = [];
        let valuesByPath = {};
        [valuesByPath, unsetPaths] = surveyHelper.removeGroupedObjects(interview, paths);
        if (returnOnly) {
            return [valuesByPath, unsetPaths];
        }
        else {
            dispatch((0, exports.startUpdateSurveyCorrectedInterview)({ valuesByPath, unsetPaths }, callback));
        }
    };
};
exports.startSurveyCorrectedRemoveGroupedObjects = startSurveyCorrectedRemoveGroupedObjects;
/**
 * Fetch an interview from server, run the audits and return the interview for
 * visualization. Review decisions are not part of this payload; they are fetched
 * with a separate, additional call to the server (see `startFetchInterviewReviewDecisions`)
 * so that refreshing audits does not depend on, or pay the cost of, fetching reviews.
 *
 * @param {*} interviewUuid The uuid of the interview to open
 * @param {*} runExtendedAuditChecks Whether to run extended audit checks (default: false)
 * @returns
 */
// TODO: unit test
const startFetchCorrectedInterviewAndAudits = (interviewUuid, runExtendedAuditChecks = false) => {
    return (dispatch, _getState) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const urlParams = new URLSearchParams();
            if (runExtendedAuditChecks) {
                urlParams.append('extended', 'true');
            }
            const queryString = urlParams.toString();
            const response = yield fetch(`/api/survey/correctInterview/${interviewUuid}${queryString ? `?${queryString}` : ''}`, {
                credentials: 'include'
            });
            if (response.status === 200) {
                const body = yield response.json();
                if (body.interview) {
                    const interview = body.interview;
                    // Unserialize surveyObjectsAndAudits if present
                    unserializeSurveyObjectsAndAudits(interview);
                    // Set the interview in the state first
                    dispatch((0, Survey_1.updateInterviewState)(interview));
                    // Review decisions are fetched separately from the interview and audits;
                    // no need to await, the UI updates whenever they arrive.
                    dispatch((0, exports.startFetchInterviewReviewDecisions)(interviewUuid));
                }
            }
        }
        catch (err) {
            surveyHelper.devLog('Error fetching interview to correct.', err);
        }
    });
};
exports.startFetchCorrectedInterviewAndAudits = startFetchCorrectedInterviewAndAudits;
/**
 * Fetch an interview from server and re-initialize the corrected_response to the
 * participant's response, but keeping the review comments.
 *
 * @param {*} interviewUuid The uuid of the interview to open
 * @param {*} callback
 * @returns
 */
// TODO: unit test
const startResetCorrectedInterview = (interviewUuid, callback = function () {
    return;
}) => {
    return (dispatch, _getState) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(`/api/survey/correctInterview/${interviewUuid}?reset=true`, {
                credentials: 'include'
            });
            if (response.status === 200) {
                const body = yield response.json();
                if (body.interview) {
                    const interview = body.interview;
                    // Unserialize surveyObjectsAndAudits if present
                    unserializeSurveyObjectsAndAudits(interview, 'for reset');
                    // Set the interview in the state and initialize navigation
                    dispatch((0, Survey_1.updateInterviewState)(interview));
                    // Review decisions are fetched separately from the interview and audits;
                    // no need to await, the UI updates whenever they arrive.
                    dispatch((0, exports.startFetchInterviewReviewDecisions)(interviewUuid));
                    // Initialize navigation for the reset interview
                    yield dispatch((0, exports.startNavigateCorrectedInterview)(undefined, callback));
                }
            }
        }
        catch (err) {
            surveyHelper.devLog('Error fetching interview to reset.', err);
        }
    });
};
exports.startResetCorrectedInterview = startResetCorrectedInterview;
/**
 * Abort review-related requests after this delay so a stalled request cannot block
 * the per-interview review-action queue forever; the abort rejects the fetch, which
 * is handled by the existing catch paths (devLog / error toast).
 */
const REVIEW_REQUEST_TIMEOUT_MS = 30000;
/** Serializes review-action API calls per interview so responses apply in send order. */
const reviewActionQueueByInterviewUuid = new Map();
/**
 * Queues review-action work behind prior in-flight actions for the same interview.
 * @param interviewUuid - Interview uuid
 * @param action - Async work to run when the queue reaches this action
 * @returns The action result
 */
const runSerializedReviewAction = (interviewUuid, action) => {
    var _a;
    const previous = (_a = reviewActionQueueByInterviewUuid.get(interviewUuid)) !== null && _a !== void 0 ? _a : Promise.resolve();
    const next = previous.catch(() => undefined).then(action);
    const tail = next.then(() => undefined, () => undefined);
    reviewActionQueueByInterviewUuid.set(interviewUuid, tail);
    tail.finally(() => {
        if (reviewActionQueueByInterviewUuid.get(interviewUuid) === tail) {
            reviewActionQueueByInterviewUuid.delete(interviewUuid);
        }
    });
    return next;
};
/**
 * Stores a review-decisions API payload in the dedicated Redux slice for the open interview.
 * Ignored when the open interview no longer matches the interview the request was sent for.
 * @param dispatch - Redux dispatch
 * @param getState - Redux state accessor
 * @param interviewUuid - Interview uuid the request was sent for
 * @param reviewDecisions - Review decisions payload from the API
 */
const dispatchReviewDecisionsPayload = (dispatch, getState, interviewUuid, reviewDecisions) => {
    const currentInterview = getState().survey.interview;
    if (!(currentInterview === null || currentInterview === void 0 ? void 0 : currentInterview.uuid) || currentInterview.uuid !== interviewUuid) {
        return;
    }
    dispatch({ type: types_1.SurveyActionTypes.SET_REVIEW_DECISIONS, reviewDecisions });
};
/**
 * Fetch review decisions for an interview from the server, as a separate API call
 * from the interview and its audits, and store them in the `reviewDecisions` Redux slice.
 *
 * @param {*} interviewUuid The uuid of the interview to fetch review decisions for
 * @returns
 */
const startFetchInterviewReviewDecisions = (interviewUuid) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield runSerializedReviewAction(interviewUuid, () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const response = yield fetch(`/api/review/decisions/${interviewUuid}`, {
                    credentials: 'include',
                    signal: AbortSignal.timeout(REVIEW_REQUEST_TIMEOUT_MS)
                });
                if (response.status === 200) {
                    const body = yield response.json();
                    applyReviewMutationResponse(dispatch, getState, interviewUuid, body, 'startFetchInterviewReviewDecisions');
                }
                else {
                    // Surface auth/permission errors (401/403…) instead of silently showing no review data
                    (0, errorHandling_1.handleHttpOtherResponseCode)(response.status, dispatch);
                }
            }
            catch (err) {
                console.error('startFetchInterviewReviewDecisions:', err);
                notifyReviewActionFetchFailed();
            }
        }));
    });
};
exports.startFetchInterviewReviewDecisions = startFetchInterviewReviewDecisions;
/**
 * Applies a review-mutation success body to Redux, or surfaces a malformed 200 response.
 * @param dispatch - Redux dispatch
 * @param getState - Redux state accessor
 * @param interviewUuid - Interview uuid the request was sent for
 * @param body - Parsed JSON body from the review mutation route
 * @param actionLabel - Label used in error logs
 */
const applyReviewMutationResponse = (dispatch, getState, interviewUuid, body, actionLabel) => {
    if (body.reviewDecisions) {
        dispatchReviewDecisionsPayload(dispatch, getState, interviewUuid, body.reviewDecisions);
        return;
    }
    console.error(`${actionLabel}: success response missing reviewDecisions`, {
        interviewUuid
    });
    sonner_1.toast.error(i18n_config_1.default.t('admin:interviewStats.errors.reviewActionFailed'));
};
/** Notifies the admin when a review mutation request fails before a response is handled. */
const notifyReviewActionFetchFailed = () => {
    sonner_1.toast.error(i18n_config_1.default.t('admin:interviewStats.errors.reviewActionFailed'));
};
/**
 * POSTs a review mutation route and applies the server payload to Redux when successful.
 * @param dispatch - Redux dispatch
 * @param getState - Redux state accessor
 * @param interviewUuid - Interview uuid the request was sent for
 * @param options - Route path, body, labels, and optional non-success handling
 */
const submitReviewMutationRequest = (dispatch, getState, interviewUuid, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    dispatch((0, LoadingState_1.incrementLoadingState)());
    try {
        const response = yield fetch(`/api/review/${options.path}/${interviewUuid}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(options.body),
            signal: AbortSignal.timeout(REVIEW_REQUEST_TIMEOUT_MS)
        });
        if (response.status === 200) {
            const body = yield response.json();
            applyReviewMutationResponse(dispatch, getState, interviewUuid, body, options.actionLabel);
            return;
        }
        if ((_a = options.onNonSuccessStatus) === null || _a === void 0 ? void 0 : _a.call(options, response, dispatch)) {
            return;
        }
        console.error(`${options.actionLabel}:`, response.status);
        (0, errorHandling_1.handleHttpOtherResponseCode)(response.status, dispatch);
    }
    catch (error) {
        console.error(`${options.actionLabel}:`, error);
        notifyReviewActionFetchFailed();
    }
    finally {
        dispatch((0, LoadingState_1.decrementLoadingState)());
    }
});
/**
 * Runs a review mutation serialized per interview when the admin interview is loaded.
 * @param getState - Redux getter for the current survey state
 * @param action - Mutation to run with the interview uuid
 */
const withSerializedInterviewReviewAction = (getState, action) => __awaiter(void 0, void 0, void 0, function* () {
    const interview = getState().survey.interview;
    if (!(interview === null || interview === void 0 ? void 0 : interview.uuid)) {
        return;
    }
    yield runSerializedReviewAction(interview.uuid, () => action(interview.uuid));
});
/**
 * Submit an approve/reject decision for one survey object during admin review.
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @param decision - Reviewer decision
 * @returns Thunk that persists the decision and refreshes review state in Redux
 */
const startSubmitObjectReview = (objectType, objectUuid, decision) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield withSerializedInterviewReviewAction(getState, (interviewUuid) => __awaiter(void 0, void 0, void 0, function* () {
            yield submitReviewMutationRequest(dispatch, getState, interviewUuid, {
                path: 'decision',
                body: { objectType, objectUuid, decision },
                actionLabel: 'Error submitting object review',
                onNonSuccessStatus: (response) => {
                    // A 409 on this route is shown to the reviewer.
                    if (response.status === 409) {
                        sonner_1.toast.error(i18n_config_1.default.t('admin:interviewMember.approveBlockedByObject'));
                        return true;
                    }
                    return false;
                }
            });
        }));
    });
};
exports.startSubmitObjectReview = startSubmitObjectReview;
/**
 * Clears the current reviewer's approve/reject decision for one survey object.
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @returns Thunk that removes the decision and refreshes review state in Redux
 */
const startClearObjectReview = (objectType, objectUuid) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield withSerializedInterviewReviewAction(getState, (interviewUuid) => __awaiter(void 0, void 0, void 0, function* () {
            yield submitReviewMutationRequest(dispatch, getState, interviewUuid, {
                path: 'clearDecision',
                body: { objectType, objectUuid },
                actionLabel: 'Error clearing object review'
            });
        }));
    });
};
exports.startClearObjectReview = startClearObjectReview;
/**
 * Ask every other reviewer who decided on a survey object to review it again,
 * after corrections were made (GitHub-style re-request review).
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @returns Thunk that persists the re-review request and refreshes review state in Redux
 */
const startRequestReReview = (objectType, objectUuid) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield withSerializedInterviewReviewAction(getState, (interviewUuid) => __awaiter(void 0, void 0, void 0, function* () {
            yield submitReviewMutationRequest(dispatch, getState, interviewUuid, {
                path: 'reReview',
                body: { objectType, objectUuid },
                actionLabel: 'Error requesting re-review'
            });
        }));
    });
};
exports.startRequestReReview = startRequestReReview;
/**
 * Admin force-approve on a survey object, overriding reviewer disagreements.
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @returns Thunk that persists the force-approve and refreshes review state in Redux
 */
const startForceApproveObject = (objectType, objectUuid) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield withSerializedInterviewReviewAction(getState, (interviewUuid) => __awaiter(void 0, void 0, void 0, function* () {
            yield submitReviewMutationRequest(dispatch, getState, interviewUuid, {
                path: 'forceApprove',
                body: { objectType, objectUuid },
                actionLabel: 'Error force-approving object',
                onNonSuccessStatus: (response) => {
                    if (response.status === 409) {
                        sonner_1.toast.error(i18n_config_1.default.t('admin:interviewMember.forceApproveNothingToOverride'));
                        return true;
                    }
                    return false;
                }
            });
        }));
    });
};
exports.startForceApproveObject = startForceApproveObject;
/**
 * Clears the current admin's force-approve on one survey object.
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @returns Thunk that removes force-approve and refreshes review state in Redux
 */
const startClearForceApproveObject = (objectType, objectUuid) => {
    return (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
        yield withSerializedInterviewReviewAction(getState, (interviewUuid) => __awaiter(void 0, void 0, void 0, function* () {
            yield submitReviewMutationRequest(dispatch, getState, interviewUuid, {
                path: 'clearForceApprove',
                body: { objectType, objectUuid },
                actionLabel: 'Error clearing force approve'
            });
        }));
    });
};
exports.startClearForceApproveObject = startClearForceApproveObject;
//# sourceMappingURL=SurveyAdmin.js.map